import React, { useEffect, useState, useCallback } from "react";
import { BarChart3, Users, Award, Target, TrendingUp } from "lucide-react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LabelList,
  Legend,
} from "recharts";
import "./Analytics.scss";
import axiosInstance from "../../axiosInstace";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";

const STATUS_COLORS = {
  active: "#10b981",
  inactive: "#f59e0b",
  expired: "#ef4444",
};

const getStatusColor = (status) => STATUS_COLORS[status] || "#6b7280";

// Matches com.hrlabs.ourdept.domain.public_tool.enums.selfdiscovery.TestAttemptType
const ATTEMPT_TYPE_META = {
  ONE_TIME: { label: "One-Time", color: "#f97316" },
  MULTIPLE: { label: "Multiple", color: "#3b82f6" },
  UNLIMITED: { label: "Unlimited", color: "#8b5cf6" },
};

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    totalTests: 0,
    totalResponses: 0,
    averageResponseRate: 0,
    avgScoreOverall: 0,
    popularTests: [],
    testPerformance: [],
    statusDistribution: [],
    attemptTypeDistribution: [],
    scoreChartData: [],
    monthlyStats: [],
  });

  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    try {
      // Two real sources of truth: the test catalog (category, attempt
      // type, expiry) and the per-test usage stats (resultCount,
      // averageScore, monthlyTrend). Everything below is derived from
      // these — no simulated/random numbers.
      const [testsResponse, statsResponse] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_API_USER_URL}/tests/getall`),
        axiosInstance.get(
          `${process.env.REACT_APP_API_USER_URL}/dashboard/singletestresponse`,
        ),
      ]);

      const details = testsResponse.data?.data || [];
      const testStats = statsResponse.data?.data || [];

      const testDetailsMap = details.reduce((acc, test) => {
        acc[test.testId] = test;
        return acc;
      }, {});

      const now = new Date();

      const testsWithDetails = testStats.map((stat) => {
        const testDetail = testDetailsMap[stat.testId] || {};
        const expiryDate = testDetail.linkExpiryDate
          ? new Date(testDetail.linkExpiryDate)
          : null;
        const isExpired = expiryDate ? expiryDate < now : false;
        const resultCount = stat.resultCount ?? 0;
        const canAttempt = testDetail.canAttempt ?? stat.canAttempt ?? false;

        return {
          testId: stat.testId,
          title:
            stat.testTitle || testDetail.testTitle || `Test #${stat.testId}`,
          category: testDetail.testCategoryType || "Uncategorized",
          attemptType: testDetail.testAttemptType || null,
          expiryDate,
          isExpired,
          canAttempt,
          resultCount,
          averageScore:
            typeof stat.averageScore === "number" ? stat.averageScore : null,
          highestScore: stat.highestScore ?? null,
          lowestScore: stat.lowestScore ?? null,
          monthlyTrend: stat.monthlyTrend || [],
          status: isExpired ? "expired" : canAttempt ? "active" : "inactive",
        };
      });

      // ── Header metrics
      const totalTests = details.length;
      const totalResponses = testsWithDetails.reduce(
        (sum, t) => sum + t.resultCount,
        0,
      );
      const scoredTests = testsWithDetails.filter(
        (t) => t.averageScore !== null,
      );
      const avgScoreOverall = scoredTests.length
        ? Math.round(
            (scoredTests.reduce((sum, t) => sum + t.averageScore, 0) /
              scoredTests.length) *
              100,
          )
        : 0;

      // ── Popular tests (top 5 by response count)
      const popularTests = [...testsWithDetails]
        .sort((a, b) => b.resultCount - a.resultCount)
        .slice(0, 5)
        .map((test) => ({
          testId: test.testId,
          name: test.title,
          responses: test.resultCount,
          avgScorePercent:
            test.averageScore !== null
              ? Math.round(test.averageScore * 100)
              : null,
          status: test.status,
        }));

      // ── Test performance ranking (all tests, for the bar chart)
      const testPerformance = testsWithDetails
        .map((test) => ({
          name:
            test.title.length > 15
              ? test.title.substring(0, 15) + "..."
              : test.title,
          testId: test.testId,
          responses: test.resultCount,
        }))
        .sort((a, b) => b.responses - a.responses)
        .slice(0, 8);

      // ── Status distribution
      const statusCounts = testsWithDetails.reduce((acc, test) => {
        acc[test.status] = (acc[test.status] || 0) + 1;
        return acc;
      }, {});
      const statusDistribution = Object.entries(statusCounts).map(
        ([status, count]) => ({
          name: status.charAt(0).toUpperCase() + status.slice(1),
          value: count,
          color: getStatusColor(status),
        }),
      );

      // ── Attempt-type mix (One-Time / Multiple / Unlimited)
      const attemptCounts = testsWithDetails.reduce((acc, test) => {
        const key = test.attemptType || "UNKNOWN";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      const attemptTypeDistribution = Object.entries(attemptCounts).map(
        ([key, count]) => ({
          name: ATTEMPT_TYPE_META[key]?.label || "Unspecified",
          value: count,
          color: ATTEMPT_TYPE_META[key]?.color || "#94a3b8",
        }),
      );

      // ── Average score by test (only tests with at least one scored attempt)
      const scoreChartData = testsWithDetails
        .filter((t) => t.averageScore !== null)
        .map((t) => ({
          name:
            t.title.length > 15 ? t.title.substring(0, 15) + "..." : t.title,
          testId: t.testId,
          avgScorePercent: Math.round(t.averageScore * 100),
        }))
        .sort((a, b) => b.avgScorePercent - a.avgScorePercent)
        .slice(0, 8);

      // ── Monthly completions/participants, summed across every test's
      // real monthlyTrend series.
      const monthlyMap = new Map();
      testsWithDetails.forEach((test) => {
        test.monthlyTrend.forEach((entry) => {
          if (!monthlyMap.has(entry.month)) {
            monthlyMap.set(entry.month, {
              month: entry.month,
              completions: 0,
              participants: 0,
            });
          }
          const agg = monthlyMap.get(entry.month);
          agg.completions += entry.completions || 0;
          agg.participants += entry.participants || 0;
        });
      });
      const monthlyStats = Array.from(monthlyMap.values());

      setAnalyticsData({
        totalTests,
        totalResponses,
        averageResponseRate:
          totalTests > 0 ? (totalResponses / totalTests).toFixed(1) : 0,
        avgScoreOverall,
        popularTests,
        testPerformance,
        statusDistribution,
        attemptTypeDistribution,
        scoreChartData,
        monthlyStats,
      });
    } catch (error) {
      console.error("❌ Failed to fetch analytics data:", error);
      if (window.showToast) {
        window.showToast("danger", "❌ Failed to fetch analytics data");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  if (loading) {
    return <LoadingScreen />;
  }

  const exportAnalytics = () => {
    const csvContent = [
      ["Metric", "Value"],
      ["Total Tests", analyticsData.totalTests],
      ["Total Responses", analyticsData.totalResponses],
      ["Average Responses / Test", analyticsData.averageResponseRate],
      ["Average Score (%)", analyticsData.avgScoreOverall],
      [],
      ["Popular Tests", ""],
      ["Test Title", "Response Count", "Avg Score (%)"],
      ...analyticsData.popularTests.map((test) => [
        test.name,
        test.responses,
        test.avgScorePercent ?? "",
      ]),
      [],
      ["Test Performance", ""],
      ["Test ID", "Title", "Responses"],
      ...analyticsData.testPerformance.map((test) => [
        test.testId,
        test.name,
        test.responses,
      ]),
      [],
      ["Monthly Trend", ""],
      ["Month", "Completions", "Participants"],
      ...analyticsData.monthlyStats.map((m) => [
        m.month,
        m.completions,
        m.participants,
      ]),
      [],
      ["Generated At", new Date().toISOString()],
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "analytics-report.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const hasScoreData = analyticsData.scoreChartData.length > 0;
  const hasMonthlyData = analyticsData.monthlyStats.some(
    (m) => m.completions > 0 || m.participants > 0,
  );
  const maxPopularResponses = Math.max(
    ...analyticsData.popularTests.map((t) => t.responses),
    1,
  );

  return (
    <div className="analytics">
      {/* Header */}
      <div className="analytics-header">
        <div className="header-content">
          <h1>Analytics Dashboard</h1>
          <p>Insights and performance metrics for your personality tests</p>
        </div>

        <div className="header-stats">
          <div className="mini-stat mini-stat--total">
            <div className="mini-stat-icon">
              <BarChart3 size={16} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">
                {analyticsData.totalTests}
              </span>
              <span className="mini-stat-label">Total Tests</span>
            </div>
          </div>

          <div className="mini-stat mini-stat--responses">
            <div className="mini-stat-icon">
              <Users size={16} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">
                {analyticsData.totalResponses}
              </span>
              <span className="mini-stat-label">Total Responses</span>
            </div>
          </div>

          <div className="mini-stat mini-stat--rate">
            <div className="mini-stat-icon">
              <Target size={16} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">
                {analyticsData.averageResponseRate}
              </span>
              <span className="mini-stat-label">Avg Responses / Test</span>
            </div>
          </div>

          <div className="mini-stat mini-stat--score">
            <div className="mini-stat-icon">
              <Award size={16} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">
                {analyticsData.avgScoreOverall}%
              </span>
              <span className="mini-stat-label">Avg Score</span>
            </div>
          </div>
        </div>

        <div className="header-actions">
          <ButtonWithIcon
            icon="lucide:refresh-cw"
            text="Refresh"
            onClick={fetchAnalyticsData}
            className="outline"
          />
          <ButtonWithIcon
            icon="lucide:download"
            text="Export"
            onClick={exportAnalytics}
            className="submit"
          />
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        {/* Monthly Trend — full width */}
        <div className="chart-container large">
          <div className="chart-header">
            <h3>Monthly Completions Trend</h3>
            <p>Completions and unique participants across all your tests</p>
          </div>
          <div className="chart-content">
            {hasMonthlyData ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart
                  data={analyticsData.monthlyStats}
                  margin={{ top: 6, right: 16, left: -14, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="completionsGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#3b82f6"
                        stopOpacity={0.35}
                      />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="participantsGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#f3f4f6" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11 }}
                    stroke="#9ca3af"
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                    stroke="#9ca3af"
                    width={26}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                    }}
                  />
                  <Legend verticalAlign="top" height={24} iconType="circle" />
                  <Area
                    type="monotone"
                    dataKey="completions"
                    name="Completions"
                    stroke="#3b82f6"
                    fill="url(#completionsGradient)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="participants"
                    name="Participants"
                    stroke="#8b5cf6"
                    fill="url(#participantsGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="chart-empty">
                <TrendingUp size={26} />
                <p>No completions recorded in the last 6 months yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* Test Performance Ranking */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Top Test Performance</h3>
            <p>Response count ranking</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                layout="vertical"
                data={analyticsData.testPerformance}
                margin={{ top: 6, right: 26, left: 10, bottom: 6 }}
              >
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#2563eb" />
                  </linearGradient>
                </defs>
                <CartesianGrid horizontal={false} stroke="#f3f4f6" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  stroke="#9ca3af"
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={110}
                  tick={{ fontSize: 11 }}
                  stroke="#6b7280"
                />
                <Tooltip
                  cursor={{ fill: "rgba(59,130,246,0.05)" }}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                  }}
                />
                <Bar
                  dataKey="responses"
                  fill="url(#barGradient)"
                  radius={[0, 8, 8, 0]}
                >
                  <LabelList
                    dataKey="responses"
                    position="right"
                    style={{ fontSize: 11, fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Distribution Donut */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Test Status Distribution</h3>
            <p>Current test lifecycle status</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={240}>
              <RechartsPieChart>
                <Pie
                  data={analyticsData.statusDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={76}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {analyticsData.statusDistribution.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                  }}
                />
                <Legend verticalAlign="bottom" height={28} />
                <text
                  x="50%"
                  y="44%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ fontSize: "22px", fontWeight: 700, fill: "#111827" }}
                >
                  {analyticsData.totalTests}
                </text>
                <text
                  x="50%"
                  y="56%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ fontSize: "11px", fill: "#6b7280" }}
                >
                  Total Tests
                </text>
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Attempt Type Distribution Donut */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Attempt Type Mix</h3>
            <p>One-Time vs. Multiple vs. Unlimited</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={240}>
              <RechartsPieChart>
                <Pie
                  data={analyticsData.attemptTypeDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={76}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {analyticsData.attemptTypeDistribution.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                  }}
                />
                <Legend verticalAlign="bottom" height={28} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Average Score by Test */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Average Score by Test</h3>
            <p>Mean score across all attempts, per test</p>
          </div>
          <div className="chart-content">
            {hasScoreData ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={analyticsData.scoreChartData}
                  margin={{ top: 6, right: 10, left: -18, bottom: 10 }}
                >
                  <CartesianGrid vertical={false} stroke="#f3f4f6" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    stroke="#9ca3af"
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                    height={44}
                  />
                  <YAxis
                    unit="%"
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    stroke="#9ca3af"
                    width={38}
                  />
                  <Tooltip
                    formatter={(value) => [`${value}%`, "Avg Score"]}
                    contentStyle={{
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                    }}
                  />
                  <Bar
                    dataKey="avgScorePercent"
                    fill="#10b981"
                    radius={[8, 8, 0, 0]}
                  >
                    <LabelList
                      dataKey="avgScorePercent"
                      position="top"
                      formatter={(v) => `${v}%`}
                      style={{ fontSize: 10, fontWeight: 600 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="chart-empty">
                <Award size={26} />
                <p>No scored attempts yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Popular Tests */}
      <div className="popular-tests-section">
        <div className="section-header">
          <h3>Top Performing Tests</h3>
          <p>Tests with the highest response rates</p>
        </div>

        {analyticsData.popularTests.length > 0 ? (
          <div className="popular-tests-grid">
            {analyticsData.popularTests.map((test, index) => (
              <div key={test.testId} className="popular-test-card">
                <div className="test-rank">#{index + 1}</div>
                <div className="test-info">
                  <div className="test-info__top">
                    <h4>{test.name}</h4>
                    <span className={`status-chip status-chip--${test.status}`}>
                      {test.status}
                    </span>
                  </div>
                  <div className="test-stats">
                    <span className="responses">
                      {test.responses} responses
                    </span>
                    {test.avgScorePercent !== null && (
                      <span className="avg-score">
                        {test.avgScorePercent}% avg
                      </span>
                    )}
                    <div className="performance-bar">
                      <div
                        className="performance-fill"
                        style={{
                          width: `${Math.min(
                            (test.responses / maxPopularResponses) * 100,
                            100,
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="chart-empty">
            <BarChart3 size={26} />
            <p>No test activity yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
